import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, Platform } from 'react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import { SafeAreaView } from 'react-native-safe-area-context';
import { commonStyles, COLORS } from '../styles/commonStyles';
import VerifyGateLogo from './icons/VerifyGateLogo';
import { useLocalization } from '../context/LocalizationContext';

interface AppLockScreenProps {
    onUnlock: () => void;
}

const AppLockScreen: React.FC<AppLockScreenProps> = ({ onUnlock }) => {
    const { t } = useLocalization();
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const authenticate = async (retry: number = 0) => {
        if (isAuthenticating) return; // Prevent multiple simultaneous attempts
        
        try {
            setIsAuthenticating(true);
            const hasHardware = await LocalAuthentication.hasHardwareAsync();
            const isEnrolled = await LocalAuthentication.isEnrolledAsync();

            if (!hasHardware || !isEnrolled) {
                // Silently unlock if no biometrics are set up.
                // In a real high-security app, you might force a PIN setup.
                setIsAuthenticating(false);
                onUnlock();
                return;
            }

            // iOS 14 specific: Add timeout handling and retry mechanism
            const isIOS14 = Platform.OS === 'ios' && Platform.Version === '14.0';
            const maxRetries = isIOS14 ? 2 : 1;
            
            const authOptions: LocalAuthentication.LocalAuthenticationOptions = {
                promptMessage: 'Unlock VerifyGate',
                disableDeviceFallback: true, // Set to true to avoid showing system UI for passcode
                cancelLabel: 'Cancel'
            };

            let result;
            if (isIOS14) {
                // iOS 14: Add timeout wrapper
                const authPromise = LocalAuthentication.authenticateAsync(authOptions);
                const timeoutPromise = new Promise<LocalAuthentication.LocalAuthenticationResult>((_, reject) => {
                    setTimeout(() => reject(new Error('Face ID authentication timed out')), 30000);
                });
                
                try {
                    result = await Promise.race([authPromise, timeoutPromise]);
                } catch (timeoutError) {
                    // Retry on timeout if retries remaining
                    if (retry < maxRetries) {
                        setRetryCount(retry + 1);
                        setIsAuthenticating(false);
                        // Wait before retry
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        return authenticate(retry + 1);
                    }
                    setIsAuthenticating(false);
                    Alert.alert(
                        'Authentication Timeout',
                        'Face ID authentication timed out. Please try again.',
                        [{ text: 'Retry', onPress: () => authenticate(0) }]
                    );
                    return;
                }
            } else {
                result = await LocalAuthentication.authenticateAsync(authOptions);
            }

            setIsAuthenticating(false);
            
            if (result.success) {
                setRetryCount(0);
                onUnlock();
            } else if (isIOS14 && retry < maxRetries) {
                // iOS 14: Retry on certain errors
                const error = result.error;
                if (error === 'UserCancel' || error === 'SystemCancel') {
                    setRetryCount(retry + 1);
                    await new Promise(resolve => setTimeout(resolve, 500));
                    authenticate(retry + 1);
                }
            }
        } catch (error: unknown) {
            setIsAuthenticating(false);
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isIOS14 = Platform.OS === 'ios' && Platform.Version === '14.0';
            if (isIOS14 && retry < 2 && (errorMessage.includes('timeout') || errorMessage.includes('network'))) {
                setRetryCount(retry + 1);
                await new Promise(resolve => setTimeout(resolve, 1000));
                authenticate(retry + 1);
            } else {
                Alert.alert(t('authenticationError') || 'Authentication Error', errorMessage || t('unexpectedError') || 'An unexpected error occurred');
            }
        }
    };

    useEffect(() => {
        // Trigger authentication immediately when the lock screen is shown
        authenticate();
    }, []);

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.content}>
                <VerifyGateLogo height={40} color={COLORS.primary} />
                <Text style={styles.title}>VerifyGate is Locked</Text>
                <Text style={styles.description}>Please authenticate to continue.</Text>
                <TouchableOpacity style={[commonStyles.button, { marginTop: 32 }]} onPress={() => authenticate()}>
                    <Text style={commonStyles.buttonText}>Unlock</Text>
                </TouchableOpacity>
            </View>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.slate100,
        justifyContent: 'center',
        alignItems: 'center',
    },
    content: {
        width: '90%',
        maxWidth: 400,
        padding: 24,
        backgroundColor: COLORS.white,
        borderRadius: 20,
        alignItems: 'center',
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 5,
    },
    title: {
        fontSize: 22,
        fontWeight: 'bold',
        color: COLORS.slate800,
        marginTop: 24,
    },
    description: {
        fontSize: 16,
        color: COLORS.slate500,
        marginTop: 8,
        textAlign: 'center',
    },
});

export default AppLockScreen;
